import { createDebug } from '../utils/debugLog'
import {
  IMAGE_MODEL_DISPLAY_IDS,
  MODEL_CAPABILITIES,
  VIDEO_MODEL_CANONICAL_ID,
  VIDEO_MODEL_DISPLAY_IDS,
} from '../generated/modelCapabilities'

const aliasMiss = createDebug('dw:model-alias:miss')

/**
 * Display-only mappings from persisted model IDs to their real model names.
 *
 * The IDs on the left ARE the values stored in YDoc and read by the backend —
 * never change them here. Keeping this mapping gives persisted aliases and
 * canonical provider IDs one stable render path without hiding which model is
 * actually selected. BYOM models intentionally keep their user-owned names.
 */
export const IMAGE_MODEL_ALIASES: Record<string, string> = Object.fromEntries(
  IMAGE_MODEL_DISPLAY_IDS.map((modelId) => [modelId, MODEL_CAPABILITIES[modelId].displayName]),
)

export const VIDEO_MODEL_ALIASES: Record<string, string> = {
  ...Object.fromEntries(
    VIDEO_MODEL_DISPLAY_IDS.flatMap((modelId) => {
      const canonicalId = VIDEO_MODEL_CANONICAL_ID[modelId] ?? modelId
      const capability = MODEL_CAPABILITIES[canonicalId]
      return capability?.modality === 'video' ? [[modelId, capability.displayName]] : []
    }),
  ),
  'grok-1.5': 'Video Legacy',
  'grok-imagine-video-1.5': 'Video Legacy I2V',
}

/**
 * Real provider model names — used in the AI Gateway section
 * where users bring their own API key and should see the actual
 * model names they're paying for.
 */
export const VIDEO_MODEL_REAL_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(VIDEO_MODEL_CANONICAL_ID).flatMap(([modelId, canonicalId]) => {
    const capability = MODEL_CAPABILITIES[canonicalId]
    return capability?.modality === 'video' ? [[modelId, capability.underlyingModel]] : []
  }),
)

export const SCRIPT_MODEL_ALIASES: Record<string, string> = {
  gemini: 'Script Lite',
  claude: 'Script Pro Plus',
}

export const SOUND_MODEL_ALIASES: Record<string, string> = {
  elevenlabs_v3: 'Sound Pro Plus',
}

function lookup(map: Record<string, string>, id: string, kind: string): string {
  const alias = map[id]
  if (alias) return alias
  aliasMiss('unknown %s id: %o', kind, id)
  return id
}

export function getImageModelLabel(id: string): string {
  return lookup(IMAGE_MODEL_ALIASES, id, 'image-model')
}

export function getImageModelAlias(id: string): string | undefined {
  return IMAGE_MODEL_ALIASES[id.trim()]
}

export function getVideoModelLabel(id: string): string {
  return lookup(VIDEO_MODEL_ALIASES, id, 'video-model')
}

/** Return the real provider name for gateway display (falls back to alias). */
export function getVideoModelRealName(id: string): string {
  return VIDEO_MODEL_REAL_NAMES[id] ?? lookup(VIDEO_MODEL_ALIASES, id, 'video-model')
}

export function getScriptModelLabel(id: string): string {
  return lookup(SCRIPT_MODEL_ALIASES, id, 'script-model')
}

export function getSoundModelLabel(id: string): string {
  return lookup(SOUND_MODEL_ALIASES, id, 'sound-model')
}
